import type { LocalProfile, UserRole } from "./auth-types";

/** Decode JWT payload (no signature verification, APIs must validate). */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function jwtExpiresAt(token: string): number | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  return typeof exp === "number" ? exp : null;
}

export function isJwtExpired(token: string, skewSeconds = 60): boolean {
  const exp = jwtExpiresAt(token);
  if (exp == null) return true;
  return Date.now() / 1000 >= exp - skewSeconds;
}

/** Matches services/api JWT issuer & audience (Holodeck-style app bearer). */
export const APP_JWT_ISS = "ccs-api";
export const APP_JWT_AUD = "ccs-web";

export interface AppJwtClaimsParsed {
  sub: string;
  email: string;
  role: UserRole;
  enrollmentSummary?: LocalProfile["enrollmentSummary"];
}

export function decodeAppJwtClaims(token: string): AppJwtClaimsParsed | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  if (payload.iss !== APP_JWT_ISS) return null;
  const aud = payload.aud;
  const audOk = aud === APP_JWT_AUD || (Array.isArray(aud) && aud.includes(APP_JWT_AUD));
  if (!audOk) return null;

  const sub = typeof payload.sub === "string" ? payload.sub : "";
  const email = typeof payload.email === "string" ? payload.email : "";
  const roleRaw = payload.role;
  const role: UserRole =
    roleRaw === "teacher" || roleRaw === "student" || roleRaw === "pending" ? roleRaw : "pending";
  const es = payload.enrollmentSummary;
  const enrollmentSummary =
    es === "active" || es === "none" || es === "archived_only" ? es : undefined;

  if (!sub) return null;
  return { sub, email, role, enrollmentSummary };
}
